import type { HealthReason, HealthState, RenewalCondition } from '@bekebi/domain';
import type { MonitoringCheck } from '@bekebi/contracts/api';
import type { Price } from '@bekebi/contracts/common';
import { formatBytes } from '../mailboxes/quota';

/**
 * Presentation of values exactly as the API returns them. These map the API's fixed codes to
 * words; they never compute a state. Times are shown in the business timezone (Africa/Lagos).
 */

export const HEALTH_LABELS: Record<HealthState, string> = {
  healthy: 'Healthy',
  needs_attention: 'Needs attention',
  unknown: 'Unknown',
};

export const HEALTH_REASON_TEXT: Record<HealthReason, string> = {
  no_service_checks: 'No checks are set up for this service yet.',
  awaiting_first_check: 'Waiting for its first check.',
  check_stale: 'The last check result is out of date, so it is not treated as current.',
  check_failing: 'A check is failing.',
  check_warning: 'A check reported a warning.',
  all_service_checks_passing: 'All checks passed recently.',
};

export const RENEWAL_CONDITION_LABELS: Record<RenewalCondition, string> = {
  upcoming: 'Upcoming',
  due_soon: 'Due soon',
  due_today: 'Due today',
  overdue: 'Overdue',
};

/**
 * A renewal term's status in plain words: the API's condition with its countdown ("Due soon · 2
 * weeks"). An overdue or due-today countdown already says everything ("Overdue by 4 days",
 * "Due today"), so it is shown alone rather than repeated.
 */
export function renewalStatusText(
  condition: RenewalCondition,
  countdown: { kind: string; label: string },
): string {
  if (countdown.kind !== 'remaining') return countdown.label;
  return `${RENEWAL_CONDITION_LABELS[condition]} · ${countdown.label}`;
}

export const CHECK_STATUS_LABELS: Record<NonNullable<MonitoringCheck['lastStatus']>, string> = {
  ok: 'Passed',
  warning: 'Warning',
  failed: 'Failed',
};

export const CHECK_KIND_TITLES: Record<MonitoringCheck['kind'], string> = {
  website_https: 'Website HTTPS check',
  nextcloud_status: 'Application status check (status.php)',
  smtp_tls: 'SMTP protocol check',
  imap_tls: 'IMAP protocol check',
  mail_dns: 'Mail DNS check (MX, SPF, DMARC, DKIM, reverse DNS)',
};

const dateTime = new Intl.DateTimeFormat('en-GB', {
  timeZone: 'Africa/Lagos',
  dateStyle: 'medium',
  timeStyle: 'short',
});
const dateOnly = new Intl.DateTimeFormat('en-GB', { timeZone: 'UTC', dateStyle: 'medium' });

const relative = new Intl.RelativeTimeFormat('en', { numeric: 'auto' });
const UNITS: [Intl.RelativeTimeFormatUnit, number][] = [
  ['year', 365 * 24 * 3600],
  ['month', 30 * 24 * 3600],
  ['week', 7 * 24 * 3600],
  ['day', 24 * 3600],
  ['hour', 3600],
  ['minute', 60],
];

/** An API timestamp relative to now ("10 minutes ago"); "just now" under a minute. */
export function formatRelative(iso: string, now: Date = new Date()): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;
  const seconds = Math.round((date.getTime() - now.getTime()) / 1000);
  for (const [unit, size] of UNITS) {
    if (Math.abs(seconds) >= size) return relative.format(Math.trunc(seconds / size), unit);
  }
  return 'just now';
}

/** An API timestamp, in Lagos time. */
export function formatDateTime(iso: string): string {
  const date = new Date(iso);
  return Number.isNaN(date.getTime()) ? iso : `${dateTime.format(date)} WAT`;
}

/** An API calendar date (YYYY-MM-DD), without any timezone shift. */
export function formatDate(ymd: string): string {
  const date = new Date(`${ymd}T00:00:00Z`);
  return Number.isNaN(date.getTime()) ? ymd : dateOnly.format(date);
}

/** Money exactly as the API sends it (a decimal string) — never converted to a float. */
export function formatPrice(price: Price): string {
  return `${price.currency} ${price.amount}`;
}

const BINARY_UNITS = ['KiB', 'MiB', 'GiB', 'TiB', 'PiB'] as const;

/**
 * Storage Bucket sizes, in binary units (1 GiB = 2^30 bytes) to two decimals, with the exact byte
 * count: e.g. 312_551_924_999 → "291.09 GiB (312,551,924,999 bytes)". Binary because the storage
 * service's own dashboard counts in these units (verified 18 Oct 2026: its "291 GB" is
 * 291.09 × 2^30 bytes) — so the two can be compared without conversion.
 */
export function formatBinaryBytes(bytes: number): string {
  const exact = `${new Intl.NumberFormat('en-GB').format(bytes)} ${bytes === 1 ? 'byte' : 'bytes'}`;
  if (bytes < 1024) return exact;
  let value = bytes;
  let unit = -1;
  while (value >= 1024 && unit < BINARY_UNITS.length - 1) {
    value /= 1024;
    unit++;
  }
  return `${value.toFixed(2)} ${BINARY_UNITS[unit]} (${exact})`;
}

/**
 * Server disk and host sizes in decimal GB to one decimal, never promoted to TB, so a reading can
 * be compared directly with `df` and the collector's own output: 1_381_747_746_048 → "1,381.7 GB".
 * Below 1 GB the usual MB/KB form is kept, so a small volume never reads "0.0 GB".
 */
export function formatGigabytes(bytes: number): string {
  if (bytes < 1_000_000_000) return formatBytes(bytes);
  const gb = new Intl.NumberFormat('en-GB', {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  }).format(bytes / 1_000_000_000);
  return `${gb} GB`;
}

/** The exact byte count, for a tooltip next to a rounded size. */
export function exactBytes(bytes: number): string {
  return `${new Intl.NumberFormat('en-GB').format(bytes)} bytes`;
}
